/**
 * SMS segment calculation, ported 1:1 from suno-backend's
 * communication/utils/sms_pricing.py so frontend and backend can never
 * disagree about what fits in a deliverable text message.
 *
 * SMS messages are split into segments based on encoding:
 * - GSM-7 encoding: 160 chars/segment (153 if concatenated)
 * - UCS-2 encoding: 70 chars/segment (67 if concatenated)
 *
 * Extended GSM characters count as 2 characters. Carriers reject anything
 * over 10 concatenated segments, so the deliverable ceiling is 1530 GSM
 * characters or 670 UCS-2 characters — NOT Twilio's 1600-character API cap.
 */

// GSM-7 basic character set
const GSM_BASIC_CHARS =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ" +
  " ÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
  "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";

// GSM-7 extended character set (these count as 2 characters each)
const GSM_EXTENDED_CHARS = "|^{}[]~\\€";

const GSM_BASIC_SET = new Set(GSM_BASIC_CHARS);
const GSM_EXTENDED_SET = new Set(GSM_EXTENDED_CHARS);

// Maximum segments for concatenated SMS (carrier limit is typically 10)
export const MAX_CONCATENATED_SEGMENTS = 10;

export const GSM_SINGLE_SEGMENT_LIMIT = 160;
export const GSM_CONCATENATED_SEGMENT_LIMIT = 153;
export const GSM_MAX_CHARS =
  MAX_CONCATENATED_SEGMENTS * GSM_CONCATENATED_SEGMENT_LIMIT; // 1530

export const UCS2_SINGLE_SEGMENT_LIMIT = 70;
export const UCS2_CONCATENATED_SEGMENT_LIMIT = 67;
export const UCS2_MAX_CHARS =
  MAX_CONCATENATED_SEGMENTS * UCS2_CONCATENATED_SEGMENT_LIMIT; // 670

export type SmsEncoding = "gsm" | "ucs2";

export interface SmsSegmentInfo {
  encoding: SmsEncoding;
  /** Effective character count: GSM extended chars count 2, UCS-2 counts UTF-16 code units (an emoji is 2). */
  charCount: number;
  /** Deliverable ceiling for this encoding (1530 GSM / 670 UCS-2). */
  maxChars: number;
  /** maxChars - charCount; negative when over the limit. */
  charsRemaining: number;
  /** 0 for an empty message. */
  segmentCount: number;
  isOverLimit: boolean;
  /** Inside the 10th (final) segment but still deliverable. */
  isWithinLastSegment: boolean;
}

export const isGsmEncoding = (text: string): boolean => {
  for (const char of text) {
    if (!GSM_BASIC_SET.has(char) && !GSM_EXTENDED_SET.has(char)) {
      return false;
    }
  }
  return true;
};

export const countGsmCharacters = (text: string): number => {
  let count = 0;
  for (const char of text) {
    count += GSM_EXTENDED_SET.has(char) ? 2 : 1;
  }
  return count;
};

export const calculateSmsSegments = (text: string): SmsSegmentInfo => {
  const isGsm = isGsmEncoding(text);
  const encoding: SmsEncoding = isGsm ? "gsm" : "ucs2";

  // UCS-2 is billed in UTF-16 code units, which is exactly String.length.
  const charCount = isGsm ? countGsmCharacters(text) : text.length;
  const singleLimit = isGsm
    ? GSM_SINGLE_SEGMENT_LIMIT
    : UCS2_SINGLE_SEGMENT_LIMIT;
  const concatenatedLimit = isGsm
    ? GSM_CONCATENATED_SEGMENT_LIMIT
    : UCS2_CONCATENATED_SEGMENT_LIMIT;
  const maxChars = isGsm ? GSM_MAX_CHARS : UCS2_MAX_CHARS;

  let segmentCount = 0;
  if (charCount > 0) {
    segmentCount =
      charCount <= singleLimit ? 1 : Math.ceil(charCount / concatenatedLimit);
  }

  const isOverLimit = charCount > maxChars;

  return {
    encoding,
    charCount,
    maxChars,
    charsRemaining: maxChars - charCount,
    segmentCount,
    isOverLimit,
    isWithinLastSegment:
      !isOverLimit && segmentCount === MAX_CONCATENATED_SEGMENTS
  };
};

export const getSmsOverLimitMessage = (info: SmsSegmentInfo): string =>
  info.encoding === "gsm"
    ? `This message is too long to deliver — carriers reject texts over ${GSM_MAX_CHARS.toLocaleString(
        "en-US"
      )} characters. Shorten it to send.`
    : `This message is too long to deliver — emoji or special characters limit texts to ${UCS2_MAX_CHARS} characters. Shorten it to send.`;
